import React from 'react';
import { localToday } from '../../utils/dateFormatting';

/** Compact KPI cards above the Solar chart. All values derived from data already
 *  fetched, so no extra GraphQL calls. */
const Card = ({ label, value, unit, sub, accent }) => (
    <div className={`flex-1 min-w-[140px] bg-white rounded-lg border border-gray-100 shadow-sm px-4 py-3 border-l-4 ${accent}`}>
        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</div>
        <div className="mt-1 flex items-baseline gap-1">
            <span className="text-2xl font-semibold text-gray-800 tabular-nums">{value}</span>
            {unit && <span className="text-xs text-gray-500">{unit}</span>}
        </div>
        {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
);

const fmt = (n, digits = 1) => {
    if (n == null || Number.isNaN(n)) return '-';
    if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const SolarKPICards = ({ solaxData = [], solarForecast = [], selectedDate }) => {
    const now = Date.now();
    const todayStr = localToday();
    const isPast = selectedDate < todayStr;
    const isToday = selectedDate === todayStr;

    // Yield: prefer the inverter's cumulative yieldtoday from the latest row;
    // fall back to integrating powerdc1+powerdc2 if missing.
    let yieldKwh = null;
    let peakW = null;
    let nowW = null;
    if (solaxData.length) {
        const last = solaxData[solaxData.length - 1];
        yieldKwh = last.yieldtoday ?? null;
        peakW = Math.max(...solaxData.map(d => (d.powerdc1 || 0) + (d.powerdc2 || 0)));
        if (isToday) nowW = (last.powerdc1 || 0) + (last.powerdc2 || 0);
    }

    // Forecast yield remaining = integrate predicted_power for future timestamps only.
    let forecastRemainKwh = null;
    let forecastPeakW = null;
    if (solarForecast.length && !isPast) {
        const future = solarForecast.filter(p => new Date(p.time).getTime() >= now);
        if (future.length) {
            // 30-min intervals -> 0.5h per sample
            forecastRemainKwh = future.reduce((sum, p) => sum + (p.predicted_power || 0) * 0.5, 0) / 1000;
            forecastPeakW = Math.max(...solarForecast.map(p => p.predicted_power || 0));
        }
    }

    return (
        <div className="flex flex-wrap gap-3 mb-4">
            <Card label={isPast ? 'Yield' : 'Yield Today'} value={fmt(yieldKwh, 2)} unit="kWh" accent="border-amber-500" />
            <Card label="Peak (Actual)" value={fmt(peakW)} unit="W" accent="border-amber-400" />
            {isToday && <Card label="Now" value={fmt(nowW)} unit="W"
                              sub={nowW > 0 ? 'generating' : 'idle'} accent="border-emerald-500" />}
            {!isPast && <Card label={isToday ? 'Forecast Remaining' : 'Forecast Yield'}
                              value={fmt(forecastRemainKwh, 1)} unit="kWh"
                              sub={forecastPeakW != null ? `peak ~${fmt(forecastPeakW)}W` : null}
                              accent="border-amber-300" />}
        </div>
    );
};

export default SolarKPICards;
